import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';

export default function ResetPassword() {
  const { token }  = useParams();
  const navigate   = useNavigate();
  const [userInfo, setUserInfo]  = useState(null);
  const [loading,  setLoading]   = useState(true);
  const [expired,  setExpired]   = useState(false);
  const [password, setPassword]  = useState('');
  const [confirm,  setConfirm]   = useState('');
  const [showPw,   setShowPw]    = useState(false);
  const [saving,   setSaving]    = useState(false);
  const [err,      setErr]       = useState('');
  const [done,     setDone]      = useState(false);

  useEffect(() => {
    api.get(`/api/auth/reset-password/${token}`)
      .then(r => setUserInfo(r.data))
      .catch(e => { if (e.response?.status === 410) setExpired(true); })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErr('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await api.post(`/api/auth/reset-password/${token}`, { password });
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setSaving(false); }
  }

  const cardStyle = {
    maxWidth: 420, margin: '0 auto', marginTop: '8vh',
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 18, padding: '36px 32px', boxShadow: '0 2px 24px rgba(0,0,0,0.35)',
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-app)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24 }}>
        <div style={cardStyle}>
          <div style={{ color: 'var(--tx-muted)', fontSize: 14 }}>Verifying link…</div>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-app)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24 }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 22, marginBottom: 8 }}>⏱</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 10 }}>Link expired</div>
          <div style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
            This reset link has expired or already been used. Request a new one from the login page.
          </div>
          <button className="btn-primary" style={{ width: '100%' }} onClick={() => navigate('/forgot-password')}>
            Request new link
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-app)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24 }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 22, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 10 }}>Password reset!</div>
          <div style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
            Your password has been updated. You can now log in with your new password.
          </div>
          <button className="btn-primary" style={{ width: '100%' }} onClick={() => navigate('/login')}>
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24 }}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
          <div className="logo-mark" style={{ width: 36, height: 36, fontSize: 14 }}>HF</div>
          <div>
            <div className="logo-name" style={{ fontSize: 15 }}>Helicon Flow</div>
            <div className="logo-sub" style={{ fontSize: 11 }}>Journey Viral</div>
          </div>
        </div>

        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 6 }}>Set a new password</div>
        <div style={{ fontSize: 13, color: 'var(--tx-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          {userInfo?.name ? `Hi ${userInfo.name} — ` : ''}Choose a new password for your account.
          {userInfo?.email && (
            <div style={{ marginTop: 4, color: 'var(--tx-muted)', fontSize: 12 }}>Account: {userInfo.email}</div>
          )}
        </div>

        {err && (
          <div style={{ padding: '10px 14px', background: 'rgba(212,85,85,0.12)', border: '1px solid rgba(212,85,85,0.3)', borderRadius: 8, fontSize: 12, color: 'var(--s-incomplete-t)', marginBottom: 16 }}>
            {err}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label className="form-label">New password</label>
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <input
              className="form-input"
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              style={{ paddingRight: 40 }}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPw(p => !p)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-muted)', fontSize: 14, padding: 4 }}
            >
              {showPw ? '🙈' : '👁'}
            </button>
          </div>

          <label className="form-label">Confirm new password</label>
          <input
            className="form-input"
            type={showPw ? 'text' : 'password'}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Repeat password"
            style={{ marginBottom: 24 }}
          />

          <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={saving}>
            {saving ? 'Saving…' : 'Reset password'}
          </button>
        </form>
      </div>
    </div>
  );
}
